import { isAudioPath } from '../../../server/src/audio-formats';

export const MAX_RECORDING_MS = 10 * 60 * 1000;
export const MAX_RECORDING_BYTES = 50 * 1024 * 1024;
export type RecordingPhase = 'idle' | 'starting' | 'recording' | 'stopping';

export function audioMessageText(text: string, paths: string[]): string {
  return (
    text.trim() ||
    (paths.some(isAudioPath) ? 'Расшифруй прикреплённое аудио и ответь по его содержанию.' : '')
  );
}

export function saveRecordingFile(file: File): void {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** One user-initiated recording. Cancellation also owns a late permission response. */
export function recordAudio(callbacks: {
  isCurrent: () => boolean;
  onChange: (phase: RecordingPhase, seconds: number) => void;
  onFile: (file: File) => void;
  onError: (message: string) => void;
}): { stop: () => void; cancel: () => void } {
  let cancelled = false;
  let stopping = false;
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let timer: ReturnType<typeof setInterval> | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let startedAt = 0;
  let bytes = 0;
  let chunks: Blob[] = [];
  const current = () => !cancelled && callbacks.isCurrent();
  const seconds = () => Math.min(600, Math.floor((Date.now() - startedAt) / 1000));
  function release(): void {
    clearInterval(timer);
    clearTimeout(deadline);
    for (const track of stream?.getTracks() ?? []) track.stop();
    stream = null;
  }
  function cancel(): void {
    if (cancelled) return;
    cancelled = true;
    chunks = [];
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        // A failed recorder must still release every microphone track.
      }
    }
    release();
    if (callbacks.isCurrent()) callbacks.onChange('idle', 0);
  }
  function fail(message: string): void {
    cancel();
    if (callbacks.isCurrent()) callbacks.onError(message);
  }
  function stop(): void {
    if (!current()) {
      cancel();
      return;
    }
    if (stopping || !recorder || recorder.state === 'inactive') return;
    stopping = true;
    callbacks.onChange('stopping', seconds());
    try {
      recorder.stop();
      release();
    } catch {
      fail('Не удалось завершить запись. Попробуйте записать сообщение снова.');
    }
  }
  callbacks.onChange('starting', 0);
  void (async () => {
    try {
      if (!globalThis.navigator?.mediaDevices?.getUserMedia || !globalThis.MediaRecorder) {
        fail(
          'Запись недоступна. Откройте сайт по HTTPS в Safari или Chrome либо прикрепите аудиофайл.',
        );
        return;
      }
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4'].find((type) =>
        MediaRecorder.isTypeSupported(type),
      );
      if (!mimeType) {
        fail('Браузер не поддерживает запись аудио. Прикрепите готовый аудиофайл.');
        return;
      }
      const granted = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!current()) {
        for (const track of granted.getTracks()) track.stop();
        return;
      }
      stream = granted;
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128_000 });
      recorder.ondataavailable = ({ data }) => {
        if (!current()) return cancel();
        bytes += data.size;
        if (bytes > MAX_RECORDING_BYTES) {
          fail('Запись превысила 50 МиБ. Запишите более короткое сообщение.');
          return;
        }
        if (data.size > 0) chunks.push(data);
      };
      recorder.onerror = () =>
        fail('Не удалось записать звук. Проверьте микрофон и попробуйте снова.');
      recorder.onstop = () => {
        if (!current()) return cancel();
        release();
        if (!stopping) return fail('Запись прервалась. Попробуйте записать сообщение снова.');
        if (bytes === 0) return fail('Запись пустая. Попробуйте записать сообщение снова.');
        const recordedType = recorder?.mimeType || mimeType;
        const container = recordedType.split(';')[0].toLowerCase();
        if (container !== 'audio/mp4' && container !== 'audio/webm') {
          fail('Браузер выбрал неподдерживаемый формат записи. Прикрепите аудиофайл.');
          return;
        }
        const extension = container === 'audio/mp4' ? 'm4a' : 'webm';
        const file = new File(chunks, `Запись-${Date.now()}.${extension}`, { type: recordedType });
        chunks = [];
        callbacks.onFile(file);
        cancelled = true;
        callbacks.onChange('idle', 0);
      };
      recorder.start(1000);
      startedAt = Date.now();
      callbacks.onChange('recording', 0);
      timer = setInterval(() => {
        if (!current()) return cancel();
        callbacks.onChange('recording', seconds());
      }, 1000);
      deadline = setTimeout(stop, MAX_RECORDING_MS);
    } catch (error) {
      if (!current()) return cancel();
      const name = error instanceof Error ? error.name : '';
      fail(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'Нет доступа к микрофону. Разрешите его в настройках сайта или прикрепите аудиофайл.'
          : name === 'NotFoundError'
            ? 'Микрофон не найден. Подключите его или прикрепите аудиофайл.'
            : 'Микрофон недоступен. Закройте другие приложения записи и попробуйте снова.',
      );
    }
  })();
  return { stop, cancel };
}
